/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/
import {Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {TabsModel} from './models/tabs.model';
import {StatefulComponent} from '../stateful/stateful.component';
import {KeyValue} from '@angular/common';
import {TabsState} from './tabs.state';

@Component({
    selector: 'app-tabs',
    templateUrl: './tabs.component.html',
    styleUrls: ['./tabs.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TabsComponent<TabsEnum, SubTabsEnum, TemplateRef> extends StatefulComponent<TabsState<TabsEnum, SubTabsEnum>>
        implements OnInit {
    @Input() tabsData: Map<TabsEnum, TabsModel<TemplateRef>>;
    @Output() selectedTabs = new EventEmitter<TabsState<TabsEnum, SubTabsEnum>>();

    get hasTabs(): boolean {
        return this.tabsData?.size > 0;
    }

    ngOnInit(): void {
        this.addActiveTabsStatus();
    }

    tabsDataCy(label: string, key: number): string {
        if (label != null && key != null) {
            return `${label}-${key}`;
        }
    }

    originalOrder(..._: KeyValue<TabsEnum | SubTabsEnum, any>[]): number {
        return 0;
    }

    addActiveTabsStatus(): void {
        if (this.tabsData != null) {
            this.tabsData.forEach((item, key) => {
                if (item.isActive) {
                    this.storeState({activeTab: key});
                }
                if (item.subTabs) {
                    item.subTabs.forEach((subTab, subTabKey: any) => {
                        if (subTab.isActive) {
                            this.storeState({activeSubTab: subTabKey});
                        }
                    });
                }
            });
        }
    }

    selectTab(tab: TabsEnum): void {
        this.tabsData.forEach((tabItem, tabKey) => {
            tabItem.isActive = tabKey === tab;
        });

        const tabValue = this.tabsData.get(tab);
        if (tabValue.subTabs != null) {
            this.selectSubTab(tab, tabValue.subTabs.keys()
                    .next().value, false);
        }

        this.storeState({activeTab: tab});
        this.selectedTabs.emit(this.state);
    }

    selectSubTab(tab: TabsEnum, subTab: SubTabsEnum, notifySubTabChange: boolean = true): void {
        this.tabsData.forEach((tabItem, tabKey) => {
            if (tabItem.subTabs != null) {
                tabItem.subTabs.forEach((subTabs, subTabKey: any) => {
                    subTabs.isActive = (tabKey === tab && subTabKey === subTab);
                });
            }
        });
        this.storeState({activeTab: tab, activeSubTab: subTab});

        // Prevent from emitting repeating changes
        if (notifySubTabChange) {
            this.selectedTabs.emit(this.state);
        }
    }

    isActive(tab: KeyValue<TabsEnum, TabsModel<TemplateRef>>): boolean {
        return (this.state.activeTab == null) ? tab.value.isActive : tab.key === this.state.activeTab;
    }

    isSubTabActive(subTab: KeyValue<SubTabsEnum, TabsModel<TemplateRef>>): boolean {
        return (this.state.activeSubTab == null) ? subTab.value.isActive : subTab.key === this.state.activeSubTab;
    }
}
